import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  FolderTree,
  CheckCircle,
  Circle,
  Search,
  Filter,
  Plus,
  Settings,
  Folder,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { apiService } from '../services/api';
import { LearningCollection, Tutorial, Category } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';

const CollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collection, setCollection] = useState<LearningCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [completedTutorialIds, setCompletedTutorialIds] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const canManage = user?.roles?.includes('admin') || 
                   user?.roles?.includes('manager') || 
                   user?.roles?.includes('instructor');

  useEffect(() => {
    if (id) {
      loadCollection();
      loadUserProgress();
    }
  }, [id, user?.id]);

  // Expand all categories by default when collection loads
  useEffect(() => {
    if (collection?.categories) {
      const allCategoryIds = new Set<string>();
      const collectIds = (cats: Category[]) => {
        cats.forEach(cat => {
          allCategoryIds.add(cat.id);
          // Debug: log category and tutorial count
          console.log('Category:', cat.name, 'Tutorials:', cat.tutorials?.length || 0, 'TutorialCount:', cat.tutorialCount);
          if (cat.subcategories) {
            collectIds(cat.subcategories);
          }
        });
      };
      collectIds(collection.categories);
      setExpandedCategories(allCategoryIds);
      // Debug: log total tutorials
      console.log('Collection loaded:', {
        totalTutorials: collection.totalTutorialCount,
        allTutorialsLength: allTutorials.length,
        categories: collection.categories.length
      });
    }
  }, [collection]);

  const loadCollection = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLearningCollection(id!);
      if (response.success && response.data) {
        setCollection(response.data);
      } else {
        toast.error('Failed to load collection');
        navigate('/tutorials');
      }
    } catch (error: any) {
      console.error('Error loading collection:', error);
      toast.error('Failed to load collection');
      navigate('/tutorials');
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    if (!user?.id || !id) return;

    try {
      const response = await apiService.getCollectionProgress(id);
      if (response.success && response.data) {
        setCompletedTutorialIds(response.data.completedTutorialIds || []);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  // Helper function to collect all tutorials from a category and its subcategories recursively
  const getAllTutorialsFromCategory = (category: Category): Tutorial[] => {
    const tutorials = category.tutorials || [];
    const subcategoryTutorials = (category.subcategories || []).flatMap(subcat => 
      getAllTutorialsFromCategory(subcat)
    );
    return [...tutorials, ...subcategoryTutorials];
  };

  // Get all tutorials (direct + from categories and subcategories)
  // Must be called before early returns to follow Rules of Hooks
  const allTutorials: Tutorial[] = useMemo(() => {
    if (!collection) return [];
    const directTutorials = collection.tutorials || [];
    const categoryTutorials = (collection.categories || []).flatMap(cat => 
      getAllTutorialsFromCategory(cat)
    );
    const combined = [...directTutorials, ...categoryTutorials];
    
    // Remove duplicates and filter out tutorials without IDs
    return combined
      .filter((tutorial) => tutorial && tutorial.id)
      .filter((tutorial, index, self) =>
        index === self.findIndex(t => t.id === tutorial.id)
      );
  }, [collection]);

  // Helper function to check if category or its tutorials match search
  const categoryMatchesSearch = (category: Category): boolean => {
    const nameMatch = category.name.toLowerCase().includes(searchQuery.toLowerCase());
    const descMatch = category.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const tutorialMatch = (category.tutorials || []).some(t => 
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const subcategoryMatch = (category.subcategories || []).some(subcat => 
      categoryMatchesSearch(subcat)
    );
    return nameMatch || descMatch || tutorialMatch || subcategoryMatch;
  };

  // Filter categories based on search (including subcategories)
  // Must be called before early returns to follow Rules of Hooks
  const filteredCategories = useMemo(() => {
    if (!collection) return [];
    if (!searchQuery.trim()) {
      return collection.categories || [];
    }
    return (collection.categories || []).filter(category => categoryMatchesSearch(category));
  }, [collection, searchQuery]);

  // Calculate progress
  // Use backend's totalTutorialCount if available, otherwise fall back to calculated value
  const totalTutorials = collection?.totalTutorialCount ?? allTutorials.length;
  const completedTutorials = allTutorials.filter(t => completedTutorialIds.includes(t.id)).length;
  const progressPercentage = totalTutorials > 0 ? (completedTutorials / totalTutorials) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Collection not found</h2>
          <button
            onClick={() => navigate('/tutorials')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Tutorials
          </button>
        </div>
      </div>
    );
  }

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              {collection.icon && (
                <div className="text-5xl">{collection.icon}</div>
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{collection.name}</h1>
                {collection.description && (
                  <p className="text-gray-600 mb-4">{collection.description}</p>
                )}
                <div className="flex items-center space-x-6 text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <BookOpen className="w-4 h-4" />
                    <span>{totalTutorials} tutorials</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <FolderTree className="w-4 h-4" />
                    <span>{collection.categoryCount || 0} categories</span>
                  </span>
                </div>
              </div>
            </div>
            {canManage && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    // Store collection ID in sessionStorage to auto-open modal
                    sessionStorage.setItem('openCollectionModal', collection.id);
                    navigate(`/admin/collections`);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-lg"
                  title="Manage tutorials and categories in this collection"
                >
                  <Settings className="w-4 h-4" />
                  <span>Manage Content</span>
                </button>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {user && totalTutorials > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Your Progress</span>
                <span className="text-sm text-gray-600">
                  {completedTutorials} of {totalTutorials} completed ({Math.round(progressPercentage)}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.5 }}
                  className="bg-primary-600 h-3 rounded-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search tutorials and categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Hierarchical Content View */}
        {filteredCategories.length > 0 ? (
          <div className="space-y-4">
            {filteredCategories.map((category) => {
              const categoryTutorials = category.tutorials || [];
              const hasSubcategories = category.subcategories && category.subcategories.length > 0;
              const isExpanded = expandedCategories.has(category.id);
              const tutorialCount = category.tutorialCount !== undefined 
                ? category.tutorialCount 
                : getAllTutorialsFromCategory(category).length;

              // Render category with nested subcategories and tutorials
              const renderCategory = (cat: Category, depth: number = 0): React.ReactNode => {
                const catTutorials = cat.tutorials || [];
                const hasSubs = cat.subcategories && cat.subcategories.length > 0;
                const isCatExpanded = expandedCategories.has(cat.id);
                const indent = depth * 24;
                
                // Debug logging
                if (depth === 0) {
                  console.log('Rendering category:', cat.name, {
                    tutorials: catTutorials.length,
                    tutorialCount: cat.tutorialCount,
                    hasSubs,
                    isExpanded: isCatExpanded,
                    subcategories: cat.subcategories?.length || 0
                  });
                }

                return (
                  <div key={cat.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {/* Category Header */}
                    <div 
              className={cn(
                        "p-4 hover:bg-gray-50 transition-colors cursor-pointer",
                        depth > 0 && "border-l-4 border-primary-200"
                      )}
                      style={{ paddingLeft: `${16 + indent}px` }}
                      onClick={() => toggleCategory(cat.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          {hasSubs && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCategory(cat.id);
                              }}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              {isCatExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
            </button>
                          )}
                          {!hasSubs && <span className="w-6" />}
                          {cat.icon && <span className="text-xl">{cat.icon}</span>}
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                            {cat.description && (
                              <p className="text-sm text-gray-600 mt-1">{cat.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>
                            {cat.tutorialCount !== undefined ? cat.tutorialCount : catTutorials.length} 
                            {' '}
                            {(cat.tutorialCount !== undefined ? cat.tutorialCount : catTutorials.length) === 1 ? 'tutorial' : 'tutorials'}
                          </span>
                          {hasSubs && (
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {cat.subcategories?.length} {cat.subcategories?.length === 1 ? 'subcategory' : 'subcategories'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Category Content (Tutorials + Subcategories) */}
                    {(isCatExpanded || !hasSubs) && (
                      <div className="border-t border-gray-100">
                        {/* Tutorials in this category */}
                        {catTutorials.length > 0 && (
                          <div className="p-4 space-y-3" style={{ paddingLeft: `${32 + indent}px` }}>
                            {catTutorials.map((tutorial) => {
                              if (!tutorial || !tutorial.id) {
                                console.warn('Tutorial missing ID:', tutorial);
                                return null;
                              }
                              const isCompleted = completedTutorialIds.includes(tutorial.id);
                              return (
                                <Link
                                  key={tutorial.id}
                                  to={`/tutorials/${tutorial.id}`}
                                  className="block"
                                >
                                  <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-primary-300 transition-all group"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex items-center space-x-2 flex-1">
                                        <BookOpen className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                                        <h4 className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                                          {tutorial.title}
                                        </h4>
                                      </div>
                                      {isCompleted && (
                                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
                                      )}
                                    </div>
                                    {tutorial.description && (
                                      <p className="text-sm text-gray-600 line-clamp-2 mb-2 ml-6">
                                        {tutorial.description}
                                      </p>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-gray-500 ml-6">
                                      <span>{tutorial.difficulty}</span>
                                      <span>{tutorial.estimatedTime}</span>
                                    </div>
                                  </motion.div>
                                </Link>
                              );
                            })}
                          </div>
                        )}

                        {/* Subcategories - Always show if expanded, even if no direct tutorials */}
                        {hasSubs && isCatExpanded && cat.subcategories && (
                          <div className="space-y-2">
                            {cat.subcategories.map(subcat => renderCategory(subcat, depth + 1))}
                          </div>
                        )}
                        
                        {/* Show message if category has tutorials but they're all in subcategories */}
                        {!hasSubs && catTutorials.length === 0 && (cat.tutorialCount || 0) > 0 && (
                          <div className="p-4 text-sm text-gray-500 text-center" style={{ paddingLeft: `${32 + indent}px` }}>
                            No tutorials directly in this category (all tutorials are in subcategories)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              return renderCategory(category);
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
            <FolderTree className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No content found</p>
            {searchQuery && (
            <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Clear search
            </button>
            )}
          </div>
        )}

        {/* Direct Tutorials (not in any category) */}
        {collection.tutorials && collection.tutorials.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Direct Tutorials ({collection.tutorials.length})
            </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {collection.tutorials.map((tutorial) => {
                if (!tutorial || !tutorial.id) return null;
                  const isCompleted = completedTutorialIds.includes(tutorial.id);
                  return (
                    <Link
                      key={tutorial.id}
                      to={`/tutorials/${tutorial.id}`}
                      className="block"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 flex-1 line-clamp-2">
                            {tutorial.title}
                          </h3>
                          {isCompleted && (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
                          )}
                        </div>
                        {tutorial.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                            {tutorial.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{tutorial.difficulty}</span>
                          <span>{tutorial.estimatedTime}</span>
                        </div>
                      </motion.div>
                    </Link>
                  );
                })}
              </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDetailPage;

